/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Dec 19 2023
 **/

import { forwardRef, useEffect } from "react";
import { ReactQuillProps } from "@rsupport/react-quill";
import styled from "@emotion/styled";
import { marked } from "marked";
import "quill/dist/quill.snow.css";

import { Each } from "@/frameworks/libraries/components";
import { Column, Row, TypographyCSS } from "@/frameworks/designStuff/components";

import { RenderItem } from "./private";
import { ErrorBoundary } from "../ErrorBoundary";
import { formatRichText } from "./private/utils";

interface ChatRendererProps extends ReactQuillProps {
	startLine?: number;
	maxLine?: number;
	chatForm?: ChatServices.Format2.Message;
	className?: string;
	onClick?: (item: ChatServices.Format2.Elements.AllRichtextElements) => void;
	emergency?: boolean;
	isUpdated?: boolean;
	files?: WebServices.Chat.Messages.Data["content"][number]["files"];
	isMe?: boolean;
	isActivity?: boolean;
}

marked.Renderer.prototype.paragraph = (text: string) => {
	return text + "\n";
};

function ChatRendererErrorFallback({ chatForm }: { chatForm?: ChatServices.Format2.Message }) {
	useEffect(() => {
		console.warn("#ChatRenderer# 채팅 메시지 구조 에러", chatForm);
	}, []);
	return null;
}

const ChatRenderer = forwardRef<HTMLDivElement, ChatRendererProps>(
	(
		{
			chatForm,
			className,
			onClick,
			startLine,
			maxLine,
			files,
			emergency = false,
			isMe = false,
			isActivity = false
		},
		ref
	) => {
		const elements = chatForm?.elements.slice(startLine, maxLine);
		const listElements = formatRichText(elements);

		// 여러줄 메세지에 대한 처리
		if (isActivity && chatForm && chatForm?.elements.length > 1) {
			const element = chatForm.elements[0];
			if ("text" in element && typeof element.text === "string") element.text = element.text.slice(0, 21) + "...";
		}

		// const emoticons = elements?.filter((element) => element.type === "emoticon");
		// const texts = elements?.filter((element) => element.type === "text");
		// const section = elements?.filter((element) => element.type === "section");

		const sortedFiles = [...(files ?? [])].sort((a, b) => {
			const aIsImage = JSON.parse(a.data).type === "image";
			const bIsImage = JSON.parse(b.data).type === "image";
			return aIsImage === bIsImage ? 0 : aIsImage ? -1 : 1;
		});

		const images = elements?.filter((element) => element.type === "image");
		const _files = elements?.filter((element) => element.type === "file");

		// elements = [...(emoticons || []), ...(texts || []), ...(section || []), ...(images || []), ...(_files || [])];

		// const parsedFiles = files?.map((file) => JSON.parse(file.data));
		// const parsedImages = parsedFiles?.filter((file) => file.type === "image");
		// const parsedFileElements = parsedFiles?.filter((file) => file.type === "file");
		// const allImages = [...(images || []), ...(parsedImages || [])];
		// const allFiles = [...(_files || []), ...(parsedFileElements || [])];

		return (
			<ErrorBoundary fallback={<ChatRendererErrorFallback chatForm={chatForm} />}>
				<StyledRenderer ref={ref} className={`w-100 h-full  ${className ?? ""} `} emergency={emergency}>
					<Each
						of={listElements}
						render={(item, index) => {
							if (item.type === "image" || item.type === "file") return null;
							return <RenderItem key={index} onClick={onClick} isActivity={isActivity} {...item} />;
						}}
					/>

					{sortedFiles && sortedFiles.length > 0 && (
						<Row css={{ width: "100%", flexWrap: "wrap", marginTop: "4px", marginBottom: "4px" }} gap={4}>
							<Each
								of={sortedFiles}
								render={(item, index) => {
									const data = JSON.parse(item.data) as
										| ChatServices.Format2.Elements.File
										| ChatServices.Format2.Elements.Image
										| ChatServices.Format2.Elements.Link;

									const singleImage =
										sortedFiles.filter((v) => JSON.parse(v.data).type === "image").length === 1;
									return (
										<RenderItem
											key={index}
											onClick={onClick}
											{...data}
											isFile
											single={singleImage}
											isSingleFile={sortedFiles.length === 1}
											isMe={isMe}
											isActivity={isActivity}
										/>
									);
								}}
							/>
						</Row>
					)}

					{images && images.length > 0 && (
						<Row css={{ width: "100%", flexWrap: "wrap", marginTop: "4px" }} gap={4}>
							<Each
								of={images}
								render={(item, index) => {
									return (
										<RenderItem
											key={index}
											onClick={onClick}
											isActivity={isActivity}
											{...item}
											single={images.length === 1}
										/>
									);
								}}
							/>
						</Row>
					)}

					{_files && _files.length > 0 && (
						<Column css={{ width: "100%", marginTop: "4px" }} gap={4}>
							<Each
								of={_files}
								render={(item, index) => {
									return (
										<RenderItem key={index} onClick={onClick} isActivity={isActivity} {...item} />
									);
								}}
							/>
						</Column>
					)}
				</StyledRenderer>
			</ErrorBoundary>
		);
	}
);

export default ChatRenderer;

const StyledRenderer = styled.div<{ emergency: boolean }>`
	position: relative;
	width: -webkit-fill-available;
	word-break: break-all;
	word-wrap: break-word;
	${({ emergency }) => TypographyCSS("Body2", emergency ? "warning_80_100" : "neutral_90_100")}

	* {
		/* cursor: default; */
	}

	a {
		text-decoration: underline;
		color: ${({ theme }) => theme.colors.deco9_50_100};
		cursor: pointer;
	}

	.ql-toolbar {
		display: none;
	}

	.mention {
		${TypographyCSS("Title2", "accent_40_100")}
	}

	code {
		color: ${({ theme }) => theme.colors.deco15_50_100};
		background-color: #f0f0f0;
		border-radius: 3px;
		font-size: 85%;
		padding: 2px 4px;
		white-space: normal;
	}

	ul,
	ol {
		${TypographyCSS("Body2")}
		padding-left: 0;
		padding-inline-start: 0.2em;
		list-style-position: inside;
	}
	ol > li {
		list-style-type: decimal;
	}
	ul > li {
		list-style-type: disc;
	}

	blockquote {
		border-left: 4px solid #ccc;
		margin-bottom: 5px;
		margin-top: 5px;

		p {
			padding-left: 16px;
		}
	}
`;
